import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useInvalidate, useTranslate } from "@refinedev/core";
import { AlertTriangle, Scale, Settings } from "lucide-react";
import { DateField, Button, CheckboxField, Field, Modal } from "@fawkes/ui";

import { Werklijst, WeergaveKeuze, useWeergave, type Kolom, type Rij } from "../components/WerklijstWeergave";
import { PensionLijstFilters, PensionLijstKop, PensionLijstMeta, PensionLijstTegel, zoekIn } from "../components/PensionLijstKop";
import { formatDate, todayIso } from "../i18n/format";
import "./PensionWerklijst.css";

type Reden = "GEEN_STARTGEWICHT" | "INTERVAL" | "DAG_VOOR_VERTREK" | "GEWOGEN";
type Signaal = "OK" | "LET_OP" | "ACTIE_NODIG";

interface Instellingen {
  intervalDagen: number;
  dagVoorVertrek: boolean;
  drempelLichtProcent: number;
  drempelZwaarProcent: number;
  grensKg: number;
}

interface Regel {
  dierId: number;
  dierNaam: string | null;
  diersoort: string | null;
  klantId: number | null;
  klantNaam: string | null;
  kennel: string | null;
  vertrek: string | null;
  laatsteGewicht: number | null;
  laatsteWeging: string | null;
  laatsteWegingDoor: string | null;
  vorigGewicht: number | null;
  verschilProcent: number | null;
  teWegen: boolean;
  reden: Reden;
  signaal: Signaal;
}

interface WegenData {
  vestigingId: number;
  datum: string;
  instellingen: Instellingen;
  telling: { teWegen: number; gewogen: number; actieNodig: number; zonderStartgewicht: number };
  regels: Regel[];
}

/** De soortfilters. "Overig" vangt alles wat geen hond of kat is, zodat er nooit iets wegvalt. */
type Soort = "ALLE" | "HOND" | "KAT" | "OVERIG";

function soortVan(diersoort: string | null): Exclude<Soort, "ALLE"> {
  const s = (diersoort ?? "").toLowerCase();
  if (s.startsWith("hond")) return "HOND";
  if (s.startsWith("kat") || s.startsWith("poes")) return "KAT";
  return "OVERIG";
}

/**
 * Het overzicht "Wegen" — wens §2 uit de wensenlijst.
 *
 * <p>Elk ingecheckt dier komt om de zoveel dagen op deze lijst; hoe vaak is een instelling per
 * vestiging, want het ene pension weegt om de vier dagen en het andere wekelijks.
 *
 * <p>Waar het echt om gaat is de kolom Verschil. De maintainer houdt daar al jaren een Excel voor bij,
 * omdat een grafiek met een knikje in de praktijk niemand in beweging krijgt: "meer dan 5% onder de
 * 40 kg, meer dan 3% daarboven" is het punt waarop iemand moet handelen. Die regel staat hier dus niet
 * als grafiek maar als oordeel — <b>Actie nodig</b>, met een rode rij die je niet over het hoofd ziet.
 *
 * <p>Wegen zelf gaat via {@code POST /api/animals/{id}/weights/registreer}: alleen het getal, de datum
 * zet de server. Wie het deed komt uit de ingelogde gebruiker.
 */
export function PensionWegen({ onOpenDier }: { onOpenDier?: (dierId: number) => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const invalidate = useInvalidate();
  const [datum, setDatum] = useState(() => todayIso());
  const [soort, setSoort] = useState<Soort>("ALLE");
  const [zoek, setZoek] = useState("");
  const [weergave, setWeergave] = useWeergave("wegen");
  const [instellingenOpen, setInstellingenOpen] = useState(false);
  const [invoer, setInvoer] = useState<Record<number, string>>({});
  const [fout, setFout] = useState<number | null>(null);

  const query = useMemo(() => ({ datum }), [datum]);
  const { data, isLoading, isError, refetch } = useCustom<WegenData>({
    url: "pension/wegen",
    method: "get",
    config: { query },
  });
  const { mutate: registreer, isLoading: bezig } = useCustomMutation();

  const wegen = data?.data;
  const alle = wegen?.regels ?? [];
  const regels = zoekIn(soort === "ALLE" ? alle : alle.filter((r) => soortVan(r.diersoort) === soort), zoek);

  const aantalPerSoort = (s: Soort) =>
    s === "ALLE" ? alle.length : alle.filter((r) => soortVan(r.diersoort) === s).length;

  function opslaan(regel: Regel) {
    const ruw = (invoer[regel.dierId] ?? "").replace(",", ".").trim();
    const gewicht = Number(ruw);
    if (!ruw || Number.isNaN(gewicht) || gewicht <= 0) {
      setFout(regel.dierId);
      return;
    }
    setFout(null);
    registreer(
      { url: `animals/${regel.dierId}/weights/registreer`, method: "post", values: { gewicht } },
      {
        onSuccess: () => {
          setInvoer((vorige) => ({ ...vorige, [regel.dierId]: "" }));
          refetch();
          // Het dierdossier toont dezelfde wegingen; anders staat daar straks een oud getal.
          invalidate({ resource: "animals", invalidates: ["all"] });
        },
        onError: () => setFout(regel.dierId),
      },
    );
  }

  // Tabel en tegels komen uit dezelfde kolommen (WerklijstWeergave).
  const kolommen: Kolom<(typeof regels)[number]>[] = [
    {
      kop: t("pensionWegen.kolom.dier"),
      cel: (r) => (
        <>
          {onOpenDier ? (
            <button
              type="button"
              className="pension-werklijst__link"
              onClick={() => onOpenDier(r.dierId)}
            >
              {r.dierNaam ?? "—"}
            </button>
          ) : (
            (r.dierNaam ?? "—")
          )}
          {r.diersoort && <span className="pension-chip">{r.diersoort}</span>}
        </>
      ),
      rol: "dier",
    },
    {
      kop: t("pensionWegen.kolom.klant"),
      cel: (r) => <>{r.klantNaam ?? "—"}</>,
      rol: "klant",
    },
    {
      kop: t("pensionWegen.kolom.kennel"),
      cel: (r) => <>{r.kennel ?? "—"}</>,
      rol: "verblijf",
    },
    {
      kop: t("pensionWegen.kolom.laatst"),
      cel: (r) => (
        <>
          {r.laatsteWeging ? formatDate(r.laatsteWeging, locale) : "—"}
          {r.laatsteWegingDoor && (
            <span className="pension-werklijst__door"> · {r.laatsteWegingDoor}</span>
          )}
        </>
      ),
    },
    {
      kop: t("pensionWegen.kolom.gewicht"),
      cel: (r) => <>{r.laatsteGewicht == null ? "—" : `${r.laatsteGewicht} kg`}</>,
      klasse: "pension-werklijst__getal",
    },
    {
      kop: t("pensionWegen.kolom.verschil"),
      cel: (r) => (
        <>
          {r.verschilProcent == null ? (
            "—"
          ) : (
            <span className="pension-werklijst__verschil" data-signaal={r.signaal}>
              {r.verschilProcent > 0 ? "+" : ""}
              {r.verschilProcent}%
              {r.signaal === "ACTIE_NODIG" && (
                <AlertTriangle size={13} aria-hidden="true" />
              )}
            </span>
          )}
        </>
      ),
      klasse: "pension-werklijst__getal",
    },
    {
      kop: t("pensionWegen.kolom.reden"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__reden" data-reden={r.reden}>
            {t(`pensionWegen.reden.${r.reden}`)}
          </span>
        </>
      ),
    },
    {
      kop: t("pensionWegen.kolom.registreren"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__invoer">
            <input
              type="text"
              inputMode="decimal"
              className="pension-werklijst__kg"
              value={invoer[r.dierId] ?? ""}
              placeholder={t("pensionWegen.kg")}
              aria-label={t("pensionWegen.kgVoor", { args: [r.dierNaam ?? ""] })}
              aria-invalid={fout === r.dierId}
              onChange={(e) =>
                setInvoer((vorige) => ({ ...vorige, [r.dierId]: e.target.value }))
              }
            />
            <Button variant="primary" disabled={bezig} onClick={() => opslaan(r)}>
              {t("pensionWegen.opslaan")}
            </Button>
          </span>
        </>
      ),
      rol: "actie",
    },
  ];
  const rij = (r: (typeof regels)[number]): Rij => ({ key: r.dierId, "data-signaal": r.signaal, "data-tewegen": r.teWegen ? "" : undefined });

  return (
    <div className="pension-werklijst fawkes-pgo">
      <PensionLijstKop icoon={<Scale size={22} />} titel={t("pensionWegen.title")} subtitel={t("pensionWegen.subtitle")} soort={{ opties: (["HOND", "KAT", "OVERIG"] as Soort[]).map((s) => ({ sleutel: s, label: t(`pensionWegen.soort.${s}`), aantal: aantalPerSoort(s) })), actief: soort, onKies: (s) => setSoort(s as Soort), totaal: aantalPerSoort("ALLE") }}>
          <PensionLijstTegel waarde={wegen?.telling.teWegen} label={t("pensionWegen.teller.teWegen")} />
          <PensionLijstTegel waarde={wegen?.telling.gewogen} label={t("pensionWegen.teller.gewogen")} />
          <PensionLijstTegel
            waarde={wegen?.telling.actieNodig}
            label={t("pensionWegen.teller.actieNodig")}
            toon={wegen && wegen.telling.actieNodig > 0 ? "rood" : undefined}
          />
          <PensionLijstTegel
            waarde={wegen?.telling.zonderStartgewicht}
            label={t("pensionWegen.teller.zonderStart")}
          />
      </PensionLijstKop>

      <PensionLijstFilters
        zoek={zoek}
        onZoek={setZoek}
        weergave={<WeergaveKeuze waarde={weergave} onKies={setWeergave} />}
        rechts={
          <div className="pension-werklijst__balk">
            <label className="po-veld werklijst-datum">
              <span>{t("pensionOverzichten.datum")}</span>
              <DateField className="pension-werklijst__datum"
                value={datum}
                onChange={(e) => e.target.value && setDatum(e.target.value)}
                aria-label={t("pensionWegen.datum")}
              />
            </label>
            <Button variant="secondary" onClick={() => setDatum(todayIso())}>
              {t("pensionWegen.vandaag")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setInstellingenOpen(true)}
              aria-label={t("pensionWegen.instellingen.knop")}
              title={t("pensionWegen.instellingen.knop")}
            >
              <Settings size={18} />
            </Button>
          </div>
        }
      />

      {isError ? (
        <p className="fawkes-muted">{t("pensionWegen.loadFailed")}</p>
      ) : (
        <>
          <PensionLijstMeta aantal={regels.length} />
          {isLoading ? (
            <p className="pension-werklijst__leeg">{t("common.loading")}</p>
          ) : regels.length === 0 ? (
            <p className="pension-werklijst__leeg">
              <Scale size={26} aria-hidden="true" />
              {t("pensionWegen.leeg")}
            </p>
          ) : (
            <Werklijst lijst="wegen" regels={regels} kolommen={kolommen} weergave={weergave} rij={rij} dierId={(r) => r.dierId} />
          )}
        </>
      )}

      {instellingenOpen && wegen && (
        <InstellingenDialoog
          waarden={wegen.instellingen}
          onClose={() => setInstellingenOpen(false)}
          onOpgeslagen={() => {
            setInstellingenOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}


/**
 * De instellingen: interval, dag vóór vertrek, en de twee drempels met hun grens. Elk pension weegt
 * anders en legt de grens anders, dus dit staat niet in de code maar per vestiging in de instellingen.
 */
function InstellingenDialoog({
  waarden,
  onClose,
  onOpgeslagen,
}: {
  waarden: Instellingen;
  onClose: () => void;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading } = useCustomMutation();
  const [form, setForm] = useState(waarden);
  const [fout, setFout] = useState(false);

  function opslaan() {
    setFout(false);
    mutate(
      { url: "pension/wegen/instellingen", method: "put", values: form },
      { onSuccess: () => onOpgeslagen(), onError: () => setFout(true) },
    );
  }

  const getal =
    (veld: keyof Instellingen) => (e: { target: { value: string } }) =>
      setForm((v) => ({ ...v, [veld]: Number(e.target.value.replace(",", ".")) || 0 }));

  return (
    <Modal open title={t("pensionWegen.instellingen.titel")} onClose={onClose}>
      <p className="fawkes-muted">{t("pensionWegen.instellingen.uitleg")}</p>
      {fout && <p className="fawkes-error" role="status">{t("pensionWegen.instellingen.mislukt")}</p>}
      <Field
        label={t("pensionWegen.instellingen.interval")}
        value={String(form.intervalDagen)}
        onChange={getal("intervalDagen")}
      />
      <CheckboxField
        label={t("pensionWegen.instellingen.dagVoorVertrek")}
        checked={form.dagVoorVertrek}
        onChange={(e) => setForm((v) => ({ ...v, dagVoorVertrek: e.target.checked }))}
      />
      <Field
        label={t("pensionWegen.instellingen.drempelLicht")}
        value={String(form.drempelLichtProcent)}
        onChange={getal("drempelLichtProcent")}
      />
      <Field
        label={t("pensionWegen.instellingen.drempelZwaar")}
        value={String(form.drempelZwaarProcent)}
        onChange={getal("drempelZwaarProcent")}
      />
      <Field
        label={t("pensionWegen.instellingen.grens")}
        value={String(form.grensKg)}
        onChange={getal("grensKg")}
      />
      <div className="pension-werklijst__dialoogknoppen">
        <Button variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" disabled={isLoading} onClick={opslaan}>
          {t("common.save")}
        </Button>
      </div>
    </Modal>
  );
}
